import styled from 'styled-components';
import {
  Table,
  type Column,
  type Row,
  type SortDescriptor,
} from '@ardoq/table';
import { autoSizeColumnProps } from './columnProps';
import { GremlinReferenceResult, GremlinResultType } from '../types';
import { formatValue } from '../utils';
import { ComponentOverviewLink } from 'components/ComponentOverviewLink/ComponentOverviewLink';
import { dispatchAction } from '@ardoq/rxbeach';
import { showGremlinResultDetails } from '../../actions';
import { SortByFn, SortById } from '@ardoq/pagination';
import { ChevronIcon } from '@ardoq/icons';
import { SortOrder } from '@ardoq/api-types';
import type { FieldLabelByName } from 'models/utils/fieldOps';
import { getGremlinTableSizeProps } from './tableSizeProps';
import { toUniqueRowIds } from '../rowIds';
import { ButtonSize, TertiaryButton } from '@ardoq/button';
import { FlexBox } from '@ardoq/layout';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';

const ID_COLUMN_ID = 'id';
const REFERENCE_COLUMN_ID = 'outVLabel/inVLabel';
const LABEL_COLUMN_ID = 'label';
const ACTIONS_COLUMN_ID = '__actions';

const StyledChevronIcon = styled(ChevronIcon)`
  transform: translateY(7px);
  line-height: 0;
`;

const ArrowSign = ({ printable }: { printable?: boolean }) =>
  printable ? <>{' -> '}</> : <StyledChevronIcon direction="right" />;

type GremlinResultsTableReferenceProps = {
  results: GremlinReferenceResult[];
  printable?: boolean;
  sortBy: SortByFn;
  sortById: SortById;
  sortOrder?: SortOrder;
  selectedColumns: string[];
  fieldLabelByName: FieldLabelByName;
  locale: Locale;
  i18nLocale: I18nLocale;
};

type CellData = {
  value: string;
  result: GremlinReferenceResult;
  printable: boolean;
};

type RowData = Record<string, CellData>;

const PlainCell = ({ value }: CellData) => <>{value}</>;

const ReferenceCell = ({ result, printable }: CellData) => (
  <span>
    <ComponentOverviewLink componentId={result.outV}>
      {result.outVLabel}
    </ComponentOverviewLink>
    <ArrowSign printable={printable} />
    <ComponentOverviewLink componentId={result.inV}>
      {result.inVLabel}
    </ComponentOverviewLink>
  </span>
);

type ShowDetailsCellProps = CellData & WithI18nLocale;

const ShowDetailsCell = ({ result, i18nLocale }: ShowDetailsCellProps) => (
  <FlexBox justify="center" height="full" align="center">
    <TertiaryButton
      buttonSize={ButtonSize.SMALL}
      onClick={() =>
        dispatchAction(
          showGremlinResultDetails({
            result,
            resultType: GremlinResultType.REFERENCE,
          })
        )
      }
    >
      {t(i18nLocale, 'search:gremlinResultsTable.showDetails', 'Show details')}
    </TertiaryButton>
  </FlexBox>
);

const getValue = ({ value }: CellData): string => value;

const getReferenceValue = ({ result }: CellData): string =>
  `${result.outVLabel} ${result.inVLabel}`;

const buildColumns = (
  selectedColumns: string[],
  fieldLabelByName: FieldLabelByName,
  printable: boolean,
  i18nLocale: I18nLocale
): Column<RowData>[] => {
  const columns: Column<RowData>[] = [
    {
      id: ID_COLUMN_ID,
      title: t(i18nLocale, 'search:gremlinResultsTable.id', 'Id'),
      cellRenderer: PlainCell,
      valueGetter: getValue,
      width: 240,
      ...autoSizeColumnProps,
    },
    {
      id: REFERENCE_COLUMN_ID,
      title: t(i18nLocale, 'search:gremlinResultsTable.reference', 'Reference'),
      cellRenderer: ReferenceCell,
      valueGetter: getReferenceValue,
      ...autoSizeColumnProps,
    },
    {
      id: LABEL_COLUMN_ID,
      title: t(i18nLocale, 'search:gremlinResultsTable.type', 'Type'),
      cellRenderer: PlainCell,
      valueGetter: getValue,
      ...autoSizeColumnProps,
    },
    ...selectedColumns.map<Column<RowData>>(columnName => ({
      id: columnName,
      title: fieldLabelByName[columnName] || columnName,
      cellRenderer: PlainCell,
      valueGetter: getValue,
      minWidth: 100,
      ...autoSizeColumnProps,
    })),
  ];
  if (!printable) {
    columns.push({
      id: ACTIONS_COLUMN_ID,
      title: '',
      cellRenderer: (cellData: CellData) => (
        <ShowDetailsCell {...cellData} i18nLocale={i18nLocale} />
      ),
      valueGetter: () => '',
      width: 150,
      suppressSorting: true,
      suppressHeaderContextMenu: true,
      resizable: false,
    });
  }
  return columns;
};

const getPropertyValue = (
  result: GremlinReferenceResult,
  columnName: string,
  locale: Locale
): string => {
  const value = (result.properties as Record<string, unknown> | undefined)?.[
    columnName
  ];
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean' || typeof value === 'string') {
    return String(formatValue(value, locale));
  }
  return String(value);
};

const toRows = (
  results: GremlinReferenceResult[],
  selectedColumns: string[],
  printable: boolean,
  locale: Locale
): Row<RowData>[] => {
  const rowIds = toUniqueRowIds(results.map(result => result.id));
  return results.map((result, index) => {
    const data: RowData = {
      [ID_COLUMN_ID]: { value: result.id, result, printable },
      [REFERENCE_COLUMN_ID]: { value: '', result, printable },
      [LABEL_COLUMN_ID]: { value: result.label, result, printable },
      [ACTIONS_COLUMN_ID]: { value: '', result, printable },
    };
    selectedColumns.forEach(columnName => {
      data[columnName] = {
        value: getPropertyValue(result, columnName, locale),
        result,
        printable,
      };
    });
    return { id: rowIds[index], data };
  });
};

const toSortDescriptor = (
  sortById: SortById,
  sortOrder: SortOrder | undefined
): SortDescriptor | null => {
  if (!sortById || !sortOrder) return null;
  return {
    column: String(sortById),
    direction: sortOrder === SortOrder.ASC ? 'asc' : 'desc',
  };
};

const GremlinResultsTableReference = ({
  results,
  printable = false,
  sortBy,
  sortById,
  sortOrder,
  selectedColumns,
  fieldLabelByName,
  locale,
  i18nLocale,
}: GremlinResultsTableReferenceProps) => (
  <Table<RowData>
    aria-label={t(
      i18nLocale,
      'search:gremlinResultsTable.referenceAriaLabel',
      'Gremlin reference results'
    )}
    rows={toRows(results, selectedColumns, printable, locale)}
    columns={buildColumns(
      selectedColumns,
      fieldLabelByName,
      printable,
      i18nLocale
    )}
    sortable={{
      descriptor: toSortDescriptor(sortById, sortOrder),
      externallySorted: true,
      onChange: descriptor => {
        if (!descriptor) return;
        const { column } = descriptor;
        if (column === REFERENCE_COLUMN_ID) {
          sortBy(['outVLabel', 'inVLabel'], REFERENCE_COLUMN_ID);
        } else {
          sortBy(column);
        }
      },
    }}
    {...getGremlinTableSizeProps(results.length)}
  />
);

export { GremlinResultsTableReference };
